/**
 * Nipper serial protocol, ported from the Nipper Serial Control reference app
 * (nipper-webserial-comms/src/index.js). Pure functions only: no DOM, no I/O.
 *
 * Frame: 0x5A | CMD | seq | len(data+7) | 0x01 | data... | CRC16 hi | CRC16 lo
 * CRC16-CCITT (poly 0x1021, init 0) over header + data. Requests use the
 * uppercase command letter, replies the lowercase one.
 */
import { NIPPER_LOGIN_LEVELS, NipperLoginLevel } from "./nipperKeys";

export const NIPPER_SERIAL_OPTIONS = {
  baudRate: 115200,
  dataBits: 8,
  stopBits: 1,
  parity: "none",
  flowControl: "none",
} as const;

export const START_BYTE = 0x5a;
const VERSION = 0x01;
const HEADER_AND_CRC = 7;

export const CMD = {
  LOGIN_REQUEST: "L",
  SERIAL_NUMBER: "N",
  VARIANT: "W",
  CELL_RUNTIME: "Q",
  FIRMWARE: "F",
  CHALLENGE: "X",
  /** Reboot into bootloader / restart (data byte 1), as in nipper-webserial-comms. */
  SWITCH_TO_BOOTLOADER: "B",
} as const;

export const REPLY = {
  ACCEPT: "a",
  REJECT: "j",
  INSUFFICIENT_PRIVILEGE: "p",
  ERROR: "e",
} as const;

export const REGIONS = ["AU", "EU"] as const;
export const BRANDS = ["Davey", "Henden", "Waterco", "WatercoNoLG"] as const;

export interface NipperFrame {
  /** Lowercase reply letter. */
  command: string;
  sequence: number;
  data: number[];
  /**
   * Whether the trailing CRC matched. The reference app never checks reply
   * CRCs, so mismatching frames are still delivered (and logged by callers).
   */
  crcOk: boolean;
}

export function crc16(bytes: ArrayLike<number>, poly = 0x1021, init = 0): number {
  let crc = init;
  for (let i = 0; i < bytes.length; i++) {
    crc ^= (bytes[i] & 0xff) << 8;
    for (let b = 0; b < 8; b++) {
      crc = ((crc << 1) ^ (crc & 0x8000 ? poly : 0)) & 0xffff;
    }
  }
  return crc & 0xffff;
}

export function buildPacket(command: string, data: number[] = [], sequence = 0): Uint8Array {
  const packet = [
    START_BYTE,
    command.toUpperCase().charCodeAt(0),
    sequence & 0xff,
    data.length + HEADER_AND_CRC,
    VERSION,
    ...data.map((d) => d & 0xff),
  ];
  const crc = crc16(packet);
  packet.push((crc >> 8) & 0xff, crc & 0xff);
  return new Uint8Array(packet);
}

/**
 * Reassembles frames from arbitrary chunks. Bytes before a 0x5A are dropped.
 * Like the reference app, frames are framed by length only; `crcOk` reports
 * the CRC check without dropping the frame.
 */
export class FrameParser {
  private buffer: number[] = [];

  push(chunk: ArrayLike<number>): NipperFrame[] {
    for (let i = 0; i < chunk.length; i++) this.buffer.push(chunk[i] & 0xff);
    const frames: NipperFrame[] = [];
    while (this.buffer.length >= HEADER_AND_CRC) {
      if (this.buffer[0] !== START_BYTE) {
        this.buffer.shift();
        continue;
      }
      const length = this.buffer[3];
      if (length < HEADER_AND_CRC) {
        this.buffer.shift();
        continue;
      }
      if (this.buffer.length < length) break;
      const frame = this.buffer.slice(0, length);
      const expected = (frame[length - 2] << 8) | frame[length - 1];
      this.buffer = this.buffer.slice(length);
      frames.push({
        command: String.fromCharCode(frame[1]).toLowerCase(),
        sequence: frame[2],
        data: frame.slice(5, length - 2),
        crcOk: crc16(frame.slice(0, length - 2)) === expected,
      });
    }
    return frames;
  }

  reset() {
    this.buffer = [];
  }
}

/** Login request payload for a level. */
export const loginRequestData = (level: NipperLoginLevel = "factory") => [NIPPER_LOGIN_LEVELS[level][0]];

/** XORs the 16-byte challenge with the level key; null when the length is wrong. */
export function solveChallenge(level: NipperLoginLevel, challenge: number[]): number[] | null {
  const key = NIPPER_LOGIN_LEVELS[level];
  if (challenge.length !== key.length - 1) return null;
  return challenge.map((byte, i) => (byte ^ key[i + 1]) & 0xff);
}

export interface NipperVariant {
  /** Raw bytes as read, needed to write back unchanged fields. */
  regionCode: number;
  variantCode: number;
  brandCode: number | null;
  region: string;
  brand: string;
  lowSalt: boolean;
  cellSize: number;
}

export function decodeVariant(data: number[]): NipperVariant | null {
  if (data.length < 4) return null;
  const brandCode = data.length >= 5 ? data[4] : null;
  return {
    regionCode: data[0],
    variantCode: data[1],
    brandCode,
    region: REGIONS[data[0]] ?? `Unknown(${data[0]})`,
    brand: brandCode === null ? "Not Set" : BRANDS[brandCode] ?? `Unknown(${brandCode})`,
    lowSalt: data[2] !== 0,
    cellSize: data[3],
  };
}

/** Fields a variant write may change. Omitted fields are written back unchanged. */
export interface VariantChange {
  regionCode?: number;
  brandCode?: number;
  lowSalt?: boolean;
  cellSize?: number;
}

/** W payload. Same packing as nipper-webserial-comms setBrand / setRegion / setCellSize / setLowSalt. */
export function encodeVariant(variant: NipperVariant, change: VariantChange = {}): number[] {
  return [
    change.regionCode ?? variant.regionCode,
    variant.variantCode,
    (change.lowSalt ?? variant.lowSalt) ? 1 : 0,
    change.cellSize ?? variant.cellSize,
    change.brandCode ?? variant.brandCode ?? 0,
  ];
}

const u32 = (d: number[], o: number) => (d[o] | (d[o + 1] << 8) | (d[o + 2] << 16) | (d[o + 3] << 24)) >>> 0;

export interface CellRuntimeCounter {
  format: "COUNTER";
  cellRuntime: number;
  lowSaltRuntime: number;
  cell2Count: number;
  cell2Runtime: number;
  cell2LowSaltRuntime: number;
}

export const RUNTIME_TYPES = { 1: "cellRuntime", 2: "lowSaltRuntime", 3: "cell2Runtime", 4: "cell2LowSaltRuntime" } as const;
export type RuntimeTypeCode = keyof typeof RUNTIME_TYPES;

export interface CellRuntimePart {
  format: "TOTAL";
  type: number;
  field: (typeof RUNTIME_TYPES)[RuntimeTypeCode] | null;
  value: number;
}

export type CellRuntimeReply = CellRuntimeCounter | CellRuntimePart | { format: "RESET_ACK"; success: boolean } | null;

/**
 * Q reply: 17 bytes `<IIBII` (newer firmware, with 2nd-cell counter), 5 bytes
 * `[type, uint32]` (older firmware, one value per reply), 1 byte reset ack.
 */
export function decodeCellRuntime(data: number[]): CellRuntimeReply {
  if (data.length === 17) {
    return {
      format: "COUNTER",
      cellRuntime: u32(data, 0),
      lowSaltRuntime: u32(data, 4),
      cell2Count: data[8],
      cell2Runtime: u32(data, 9),
      cell2LowSaltRuntime: u32(data, 13),
    };
  }
  if (data.length === 5) {
    const type = data[0];
    return { format: "TOTAL", type, field: RUNTIME_TYPES[type as RuntimeTypeCode] ?? null, value: u32(data, 1) };
  }
  if (data.length === 1) return { format: "RESET_ACK", success: data[0] !== 0 };
  return null;
}

export const toHex = (data: ArrayLike<number>) =>
  Array.from(data, (b) => (b & 0xff).toString(16).padStart(2, "0")).join("");

const isPrintable = (data: number[]) =>
  data.length > 0 && data.every((b) => b === 0 || (b >= 0x20 && b <= 0x7e));

const asciiOf = (data: number[]) =>
  String.fromCharCode(...data.filter((b) => b !== 0)).trim();

/**
 * N reply. The format is not documented in the reference app, so: printable
 * ASCII is used as-is, otherwise the bytes as uppercase hex. Raw hex is
 * always stored alongside for later validation.
 */
export function decodeSerial(data: number[]): string | null {
  if (data.length === 0) return null;
  if (isPrintable(data)) return asciiOf(data) || null;
  return toHex(data).toUpperCase();
}

/** F reply (format undocumented): ASCII if printable, else dotted bytes (e.g. 1.2.3). */
export function decodeFirmware(data: number[]): string | null {
  if (data.length === 0) return null;
  if (isPrintable(data)) return asciiOf(data) || null;
  return data.slice(0, 4).join(".");
}
